import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import { FormData } from './types';
import { saveAdToFirebase, backgroundUpdateAdPaymentStatus, backgroundUpdateAdData, getAdFromFirebase } from './firebase-service';
import { saveUserData, loadSavedData, saveCurrentAdId, getCurrentAdId, createAutoSave } from './utils/localStorage';
// Removed unused validation imports
import { AD_PRICE_FORMATTED, AD_PRICE } from './utils/config';
import { useMessages } from './hooks/useMessages';
import { useEasterEggs, getEasterEggMessage } from './hooks/useEasterEggs';
import { stripeService } from './services/stripe-service';
import AdForm from './components/AdForm';
import PaymentModal from './components/PaymentModal';
import RotatingHeadline from './components/RotatingHeadline';
import MessageSystem from './components/MessageSystem';
import EasterEgg from './components/EasterEgg';

const headlines = [
  {
    text: "📣 Advertise on Neodia — Reach 57,000+ readers now",
    emoji: "📣",
    highlight: "57,000+"
  },
  {
    text: "🚀 Launch Your Brand — Get 45K+ daily views",
    emoji: "🚀",
    highlight: "45K+"
  },
  {
    text: "💡 Smart Advertising — 8.2% engagement guaranteed",
    emoji: "💡",
    highlight: "8.2%"
  },
  {
    text: "🎯 Target France — 68% of our audience lives there",
    emoji: "🎯",
    highlight: "68%"
  },
  {
    text: "👥 Professional Audience — 82% employed readers",
    emoji: "👥",
    highlight: "82%"
  },
  {
    text: "⚡ Instant Results — Post your ad in 60 seconds",
    emoji: "⚡",
    highlight: "60 seconds"
  },
  {
    text: `🔥 Premium Placement — Only ${AD_PRICE_FORMATTED} for 48 hours`,
    emoji: "🔥",
    highlight: AD_PRICE_FORMATTED
  }
];

interface CryptoPaymentResponse {
  id: string;
  invoice_url: string;
  [key: string]: any;
}

function App() {
  const [formData, setFormData] = useState<FormData>({
    adContent: '',
    adTextContent: '',
    buttonText: '',
    buttonLink: '',
    image: null,
    imagePreview: null
  });

  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [currentAdId, setCurrentAdId] = useState<string | null>(null);
  const [currentShortAdId, setCurrentShortAdId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [easterEgg, setEasterEgg] = useState<{ type: string; message: string } | null>(null);

  // Message system
  const { messages, showSuccess, showError, removeMessage } = useMessages();

  // Easter eggs
  const { handleTitleClick, handleTextEasterEgg } = useEasterEggs((type: string) => {
    const message = getEasterEggMessage(type);
    setEasterEgg({ type, message });
  });

  // Create auto-save function
  const autoSave = useCallback(() => {
    const debouncedSave = createAutoSave(() => {
      saveUserData(formData);
    }, 1000);
    debouncedSave();
  }, [formData]);

  // Load saved data on initialization
  useEffect(() => {
    const savedData = loadSavedData();
    if (Object.keys(savedData).length > 0) {
      setFormData(prevData => ({
        ...prevData,
        ...savedData
      }));
      console.log('Loaded saved user data');
    }
    
    // Load current ad ID if exists
    const savedAdId = getCurrentAdId();
    if (savedAdId) {
      setCurrentAdId(savedAdId);
    }
  }, []);

  // Auto-save form data whenever it changes
  useEffect(() => {
    // Skip auto-save on initial load
    if (formData.adContent || formData.adTextContent || formData.buttonText || formData.buttonLink || formData.imagePreview) {
      autoSave();
    }
  }, [formData, autoSave]);

  // Check for text easter eggs when ad content changes
  useEffect(() => {
    if (formData.adTextContent) {
      handleTextEasterEgg(formData.adTextContent);
    }
  }, [formData.adTextContent, handleTextEasterEgg]);

  // Removed validateForm function - validation is now disabled to allow immediate payment

  const handleSubmit = async () => {
    // Remove validation check - allow customers to proceed to payment immediately
    // if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Generate ad ID immediately for immediate payment flow
      const tempAdId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setCurrentAdId(tempAdId);
      saveCurrentAdId(tempAdId);
      
      // Show payment modal immediately - don't wait for Firebase/Telegram
      showSuccess('Choose your payment method to proceed.');
      setIsPaymentModalOpen(true);
      
      // Run Firebase saving and Telegram notification in background
      backgroundSaveToFirebase(tempAdId, formData);
      
    } catch (error) {
      console.error('Error during ad creation:', error);
      showError('Failed to prepare ad. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Background function to save to Firebase and send Telegram notification
  const backgroundSaveToFirebase = async (tempAdId: string, formData: FormData) => {
    try {
      console.log('🔄 Starting background save for ad:', tempAdId);
      
      // Save ad to Firebase in background
      const realAdId = await saveAdToFirebase(formData, tempAdId);
      
      if (realAdId) {
        // Update the real ad ID once Firebase save is complete
        setCurrentAdId(realAdId);
        saveCurrentAdId(realAdId);
        
        // Fetch the ad data to get the short ID
        const adData = await getAdFromFirebase(realAdId);
        if (adData && adData.shortId) {
          setCurrentShortAdId(adData.shortId);
        }
        
        console.log('✅ Background save completed for ad:', realAdId);
      } else {
        console.error('❌ Background save failed for ad:', tempAdId);
        // Don't show error to user since they're already in payment flow
        // The payment webhook will handle any missing data
      }
    } catch (error) {
      console.error('❌ Background save error:', error);
      // Don't interrupt user flow
    }
  };

  const handlePayment = async (method: 'crypto' | 'card', telegramUsername?: string): Promise<{ success: boolean; url?: string; error?: string }> => {
    if (!currentAdId) return { success: false, error: 'No ad ID found' };
    
    try {
      console.log('🚀 Starting immediate payment flow for:', method);
      
      if (method === 'crypto') {
        // Create crypto payment immediately
        const paymentResult: CryptoPaymentResponse | null = await createCryptoPayment(currentAdId);
        if (paymentResult) {
          showSuccess('Cryptocurrency payment initiated! Complete payment in the new tab.');
          
          // Update payment status in background (non-blocking)
          backgroundUpdatePaymentStatus(currentAdId, {
            status: 'payment_initiated',
            method: 'crypto',
            details: {
              type: 'cryptocurrency',
              invoiceId: paymentResult.id,
              invoiceUrl: paymentResult.invoice_url,
              orderId: `NEODIA_AD_${currentAdId}`,
              amount: AD_PRICE,
              currency: 'USD',
              initiatedAt: new Date().toISOString()
            }
          }, telegramUsername);
          
          // Open payment immediately
          const redirectWindow = window.open(paymentResult.invoice_url, '_blank', 'noopener,noreferrer');
          
          // Fallback if popup was blocked
          if (!redirectWindow || redirectWindow.closed || typeof redirectWindow.closed === 'undefined') {
            navigator.clipboard.writeText(paymentResult.invoice_url).then(() => {
              showSuccess('Payment link copied to clipboard! Paste it in a new tab to complete payment.');
            }).catch(() => {
              showError(`Payment link: ${paymentResult.invoice_url} - Please copy this link manually.`);
            });
          }
          
          return { success: true, url: paymentResult.invoice_url };
        }
        return { success: false, error: 'Failed to create crypto payment' };
      } else {
        // Handle card payment with Stripe
        const sessionData = await stripeService.createCheckoutSession(currentAdId, formData);
        if (sessionData) {
          showSuccess('Card payment initiated! Complete payment in the new tab.');
          
          // Update payment status in background (non-blocking)
          backgroundUpdatePaymentStatus(currentAdId, {
            status: 'payment_initiated',
            method: 'card',
            details: {
              type: 'stripe_checkout',
              sessionId: sessionData.sessionId,
              amount: AD_PRICE,
              currency: 'USD',
              initiatedAt: new Date().toISOString()
            }
          }, telegramUsername);
          
          // Open Stripe checkout immediately
          const redirectWindow = window.open(sessionData.url, '_blank', 'noopener,noreferrer');
          
          // Fallback if popup was blocked
          if (!redirectWindow || redirectWindow.closed || typeof redirectWindow.closed === 'undefined') {
            navigator.clipboard.writeText(sessionData.url).then(() => {
              showSuccess('Payment link copied to clipboard! Paste it in a new tab to complete payment.');
            }).catch(() => {
              showError(`Payment link: ${sessionData.url} - Please copy this link manually.`);
            });
          }
          
          return { success: true, url: sessionData.url };
        }
        return { success: false, error: 'Failed to create card payment' };
      }
    } catch (error) {
      console.error('Payment error:', error);
      showError('Payment failed. Please try again or contact @neodia_ad in telegram.');
      return { success: false, error: 'Payment failed' };
    }
  };

  // Background function to update payment status
  const backgroundUpdatePaymentStatus = async (adId: string, paymentData: any, telegramUsername?: string) => {
    try {
      console.log('🔄 Background payment status update for:', adId);
      
      // Update ad with telegram username if provided
      if (telegramUsername) {
        backgroundUpdateAdData(adId, { telegramUsername });
      }
      
      // Update payment status
      backgroundUpdateAdPaymentStatus(adId, paymentData);
      
      console.log('✅ Background payment status update initiated for:', adId);
    } catch (error) {
      console.error('❌ Background payment status update failed:', error);
      // Don't interrupt user flow
    }
  };

  const createCryptoPayment = async (adId: string): Promise<CryptoPaymentResponse | null> => {
    try {
      const invoiceData = {
        price_amount: AD_PRICE,
        price_currency: 'USD',
        order_id: `NEODIA_AD_${adId}`,
        order_description: `Neodia Telegram Ad: ${formData.adTextContent.substring(0, 100)}...`,
        success_url: `${window.location.origin}/payment-success?ad_id=${adId}&payment_method=crypto`,
        cancel_url: `${window.location.origin}/payment-cancel?ad_id=${adId}&payment_method=crypto`
      };

      const apiKey = process.env.REACT_APP_NOWPAYMENTS_API_KEY;
      if (!apiKey) {
        throw new Error('NOWPayments API key not configured. Please set REACT_APP_NOWPAYMENTS_API_KEY in your environment variables.');
      }

      const response = await fetch('https://api.nowpayments.io/v1/invoice', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey
        },
        body: JSON.stringify(invoiceData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('NOWPayments API error:', response.status, errorText);
        throw new Error(`NOWPayments API error: ${response.status} - ${errorText}`);
      }

      const responseData = await response.json();
      console.log('NOWPayments invoice created:', responseData);
      
      // Validate response has required fields
      if (!responseData.invoice_url || !responseData.id) {
        throw new Error('Invalid response from NOWPayments API - missing invoice URL or ID');
      }
      
      return responseData;
    } catch (error) {
      console.error('Crypto payment error:', error);
      return null;
    }
  };

  const handleEasterEggClose = () => {
    setEasterEgg(null);
  };

  return (
    <div className="container">
      <header className="header">
        <h1 className="main-title" onClick={handleTitleClick}>
          <RotatingHeadline headlines={headlines} />
        </h1>
        <p className="subtitle">Fill in your ad, see how it looks, and post instantly.</p>
      </header>

      <AdForm 
        formData={formData}
        setFormData={setFormData}
      />

      <div className="urgency-message">
        <span className="urgency-icon">🚨</span>
        Your ad will reach 57,000+ Telegram readers. Limited slots!
      </div>

      <div className="cta-container">
        <button 
          className="cta-button" 
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          <span className="cta-icon">🚀</span>
          {isSubmitting ? 'Saving...' : `Post My Ad Now – ${AD_PRICE_FORMATTED}`}
        </button>
        <div className="timing-info">
          <span className="timing-detail">⏱️ 48-hour placement</span>
          <span className="timing-separator">•</span>
          <span className="timing-detail">🔒 2-hour exclusivity</span>
        </div>
        <div className="main-support-message">
          <p>Need help? Contact us <a href="https://t.me/neodia_ad" target="_blank" rel="noopener noreferrer">@neodia_ad</a> in telegram - reply in minutes</p>
        </div>
      </div>

      <PaymentModal 
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onPayment={handlePayment}
        shortAdId={currentShortAdId || undefined}
      />

      <MessageSystem 
        messages={messages} 
        onRemoveMessage={removeMessage}
      />

      {easterEgg && (
        <EasterEgg 
          type={easterEgg.type}
          message={easterEgg.message}
          onClose={handleEasterEggClose}
        />
      )}
    </div>
  );
}

export default App;
